/**
 * Single navigation config (WP-P1). Drives LeftNavRail (desktop), NavDrawer
 * (mobile), the command palette and the Settings sidebar. Items are filtered
 * by `minRole` and optional `featureFlag` (from GET /features).
 *
 * Icons are lucide component references (not elements) so this module has
 * no JSX and can be imported anywhere.
 */
import type { LucideIcon } from 'lucide-react'
import {
  Sun, Package, Users, Gauge, Settings, Globe, LayoutDashboard, ClipboardList,
  AlertTriangle, CalendarClock, ListChecks, Truck, Layers, MapPin, List,
  Smartphone, Wrench, Clock, Sparkles, Mail, RefreshCw, Shield, ToggleLeft, Zap,
  Map as MapIcon, Route,
} from 'lucide-react'
import { hasMinimumRole } from './utils/roles'
import type { FeatureFlags } from './types/api'

export type NavGroup = 'main' | 'platform' | 'legacy'

export interface NavItem {
  id: string
  /** i18n key in messages/en.json (nav.*); `label` is the English fallback. */
  labelId: string
  label: string
  icon: LucideIcon
  path: string
  minRole: string
  /** Only shown when this feature flag is true. */
  featureFlag?: string
  group: NavGroup
  /** Optional keyboard hint for the command palette. */
  shortcut?: string
  /** Extra path prefixes that should highlight this item. */
  matchPrefixes?: string[]
}

export const NAV_ITEMS: NavItem[] = [
  { id: 'today', labelId: 'nav.today', label: 'Today', icon: Sun, path: '/today', minRole: 'DISPATCHER', group: 'main', shortcut: 'G T' },
  { id: 'loads', labelId: 'nav.loads', label: 'Loads', icon: Package, path: '/loads', minRole: 'DISPATCHER', group: 'main', shortcut: 'G L' },
  { id: 'drivers', labelId: 'nav.drivers', label: 'Drivers', icon: Users, path: '/drivers', minRole: 'DISPATCHER', group: 'main', shortcut: 'G D' },
  { id: 'performance', labelId: 'nav.performance', label: 'Performance', icon: Gauge, path: '/performance', minRole: 'DISPATCHER', group: 'main', shortcut: 'G P' },
  { id: 'settings', labelId: 'nav.settings', label: 'Settings', icon: Settings, path: '/settings', minRole: 'DISPATCHER', group: 'main', shortcut: 'G S', matchPrefixes: ['/admin'] },
  { id: 'platform', labelId: 'nav.platform', label: 'Platform', icon: Globe, path: '/platform', minRole: 'SUPERADMIN', group: 'platform', matchPrefixes: ['/superadmin'] },
  // Deprecated surfaces — hidden unless features.legacy_* is on.
  { id: 'legacy-dashboard', labelId: 'nav.legacyDashboard', label: 'Dashboard (legacy)', icon: LayoutDashboard, path: '/legacy/dashboard', minRole: 'DISPATCHER', featureFlag: 'legacy_dashboard', group: 'legacy' },
  { id: 'legacy-assignments', labelId: 'nav.legacyAssignments', label: 'Assignments (legacy)', icon: ClipboardList, path: '/legacy/assignments', minRole: 'DISPATCHER', featureFlag: 'legacy_assignments_page', group: 'legacy' },
  { id: 'legacy-cascades', labelId: 'nav.legacyCascades', label: 'Cascades (legacy)', icon: AlertTriangle, path: '/legacy/cascades', minRole: 'DISPATCHER', featureFlag: 'legacy_cascades_page', group: 'legacy' },
  { id: 'legacy-duty-schedule', labelId: 'nav.legacyDutySchedule', label: 'Duty Schedule (legacy)', icon: CalendarClock, path: '/legacy/duty-schedule', minRole: 'DISPATCHER', featureFlag: 'legacy_duty_schedule', group: 'legacy' },
]

export function visibleNavItems(role: string | null | undefined, features: FeatureFlags | undefined): NavItem[] {
  return NAV_ITEMS.filter(item => {
    if (role && !hasMinimumRole(role, item.minRole)) return false
    if (!role && item.minRole !== 'DRIVER') return false
    if (item.featureFlag && !(features?.[item.featureFlag] ?? false)) return false
    return true
  })
}

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (pathname === item.path || pathname.startsWith(item.path + '/')) return true
  return (item.matchPrefixes ?? []).some(p => pathname === p || pathname.startsWith(p + '/'))
}

/* ── Settings sidebar (BLUEPRINT §2.5) ─────────────────────────────────── */

export interface SettingsNavItem {
  id: string
  labelId: string
  label: string
  icon: LucideIcon
  /** path under /settings */
  path: string
  minRole: string
  /** legacy ?tab= value in /admin, for redirects */
  legacyTab?: string
  /** true for screens that are new in the revamp */
  isNew?: boolean
  keywords?: string
}

export interface SettingsNavGroup {
  id: string
  labelId: string
  label: string
  items: SettingsNavItem[]
}

export const SETTINGS_NAV: SettingsNavGroup[] = [
  { id: 'setup', labelId: 'settings.group.setup', label: 'Get started', items: [
    { id: 'setup', labelId: 'settings.setup', label: 'Setup checklist', icon: ListChecks, path: '/settings/setup', minRole: 'MANAGER', isNew: true, keywords: 'onboarding checklist progress' },
  ]},
  { id: 'organisation', labelId: 'settings.group.organisation', label: 'Organisation', items: [
    { id: 'users', labelId: 'settings.users', label: 'Users & roles', icon: Users, path: '/settings/users', minRole: 'ADMIN', legacyTab: 'users' },
    { id: 'logistics-groups', labelId: 'settings.logisticsGroups', label: 'Logistics groups', icon: Layers, path: '/settings/logistics-groups', minRole: 'MANAGER', legacyTab: 'logistics-groups', keywords: 'region depot' },
    { id: 'drivers', labelId: 'settings.drivers', label: 'Drivers', icon: Users, path: '/settings/drivers', minRole: 'DISPATCHER', legacyTab: 'drivers' },
    { id: 'facilities', labelId: 'settings.facilities', label: 'Facilities & geofences', icon: MapPin, path: '/settings/facilities', minRole: 'MANAGER', legacyTab: 'facilities' },
    { id: 'vehicles', labelId: 'settings.vehicles', label: 'Vehicles & trailers', icon: Truck, path: '/settings/vehicles', minRole: 'MANAGER', isNew: true },
    { id: 'devices', labelId: 'settings.devices', label: 'Devices', icon: Smartphone, path: '/settings/devices', minRole: 'DISPATCHER', legacyTab: 'devices' },
    { id: 'equipment', labelId: 'settings.equipment', label: 'Equipment types', icon: Wrench, path: '/settings/equipment', minRole: 'MANAGER', legacyTab: 'equipment' },
    { id: 'event-types', labelId: 'settings.eventTypes', label: 'Driver event types', icon: List, path: '/settings/event-types', minRole: 'MANAGER', legacyTab: 'event-types' },
  ]},
  { id: 'operations', labelId: 'settings.group.operations', label: 'Operations rules', items: [
    { id: 'autonomy', labelId: 'settings.autonomy', label: 'Autonomy & approvals', icon: Zap, path: '/settings/autonomy', minRole: 'ADMIN', isNew: true, keywords: 'auto-plan approval acceptance' },
    { id: 'tracking', labelId: 'settings.tracking', label: 'Tracking & geofence', icon: MapIcon, path: '/settings/tracking', minRole: 'ADMIN', legacyTab: 'configs' },
    { id: 'eta', labelId: 'settings.eta', label: 'ETA & delay alerts', icon: Clock, path: '/settings/eta', minRole: 'ADMIN' },
    { id: 'knock-on', labelId: 'settings.knockOn', label: 'Knock-on delays', icon: Route, path: '/settings/knock-on', minRole: 'ADMIN', keywords: 'cascade' },
    { id: 'hos', labelId: 'settings.hos', label: 'Hours of Service', icon: Clock, path: '/settings/hos', minRole: 'MANAGER', legacyTab: 'hos' },
    { id: 'ai-rules', labelId: 'settings.aiRules', label: 'AI assignment rules', icon: Sparkles, path: '/settings/ai-rules', minRole: 'MANAGER', legacyTab: 'ai-rules' },
    { id: 'debriefer', labelId: 'settings.debriefer', label: 'Debriefer rules', icon: Sparkles, path: '/settings/debriefer', minRole: 'ADMIN', legacyTab: 'debriefer' },
    { id: 'inspections', labelId: 'settings.inspections', label: 'Inspection checklists', icon: ClipboardList, path: '/settings/inspections', minRole: 'ADMIN', isNew: true, keywords: 'DVIR pre-trip post-trip' },
    { id: 'fleet-mode', labelId: 'settings.fleetMode', label: 'Fleet mode', icon: Truck, path: '/settings/fleet-mode', minRole: 'ADMIN', isNew: true },
    { id: 'loads', labelId: 'settings.loadPlans', label: 'Load plans (import)', icon: Package, path: '/settings/loads', minRole: 'MANAGER', legacyTab: 'loads' },
  ]},
  { id: 'integrations', labelId: 'settings.group.integrations', label: 'Integrations', items: [
    { id: 'tms', labelId: 'settings.tms', label: 'TMS', icon: RefreshCw, path: '/settings/tms', minRole: 'ADMIN', legacyTab: 'tms' },
    { id: 'email', labelId: 'settings.email', label: 'Email', icon: Mail, path: '/settings/email', minRole: 'ADMIN', legacyTab: 'email' },
    { id: 'maps', labelId: 'settings.maps', label: 'Maps', icon: MapIcon, path: '/settings/maps', minRole: 'ADMIN', isNew: true },
  ]},
  { id: 'privacy', labelId: 'settings.group.privacy', label: 'Privacy & GDPR', items: [
    { id: 'gdpr', labelId: 'settings.gdpr', label: 'Privacy & GDPR', icon: Shield, path: '/settings/gdpr', minRole: 'ADMIN', legacyTab: 'gdpr' },
  ]},
  { id: 'features', labelId: 'settings.group.features', label: 'Features', items: [
    { id: 'features', labelId: 'settings.features', label: 'Features & deprecations', icon: ToggleLeft, path: '/settings/features', minRole: 'ADMIN', isNew: true },
  ]},
]

export const ALL_SETTINGS_ITEMS: SettingsNavItem[] = SETTINGS_NAV.flatMap(g => g.items)

export function visibleSettingsNav(role: string | null | undefined): SettingsNavGroup[] {
  return SETTINGS_NAV
    .map(g => ({ ...g, items: g.items.filter(i => !role || hasMinimumRole(role, i.minRole)) }))
    .filter(g => g.items.length > 0)
}

/** Map a legacy `/admin?tab=` value to its new /settings path. */
export function settingsPathForLegacyTab(tab: string | null | undefined): string | null {
  if (!tab) return null
  const hit = ALL_SETTINGS_ITEMS.find(i => i.legacyTab === tab)
  return hit?.path ?? null
}

/** Legacy → new route map (BLUEPRINT §2.7). */
export const LEGACY_REDIRECTS: Record<string, string> = {
  '/dashboard': '/today?view=map',
  '/assignments': '/loads?view=needs-driver',
  '/cascades': '/today?tab=exceptions&type=KNOCK_ON',
  '/duty-schedule': '/drivers?view=board',
  '/pulse': '/performance',
  '/reports': '/performance?tab=reports',
  '/superadmin': '/platform',
}
